import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Alert, Button, Stack } from "@mui/material";
import React, { type ReactNode } from "react";
import { EmailMethod } from "../api/types";
import { useGlobalState } from "../components/globalState";
import { SnoozeButton, useBannerSnoozed } from "./banner-snooze";

const providerSlug = (method: EmailMethod | undefined): string => {
	if (method === EmailMethod.OUTLOOK) return "outlook";
	if (method === EmailMethod.GMAIL) return "gmail";
	return "imap";
};

/**
 * Tells the account owner their mailbox has stopped syncing — once it really has.
 *
 * Gated on the server's `email_sync_error_is_persistent`, not on the raw error
 * string: mail sync runs every ten minutes and providers fail single cycles that
 * repair themselves. On 2026-09-22 a transient MS Graph "Invalid token lifetime."
 * 401 put this banner, with the provider's own JSON truncated mid-value and a
 * FIX CREDENTIALS button, across every screen of seven Outlook accounts whose
 * credentials were perfectly good — and the next cycle cleared it unattended.
 *
 * So the raw text does not come here any more. A failure that has outlasted the
 * threshold is a real one, which is why the reconnect action stays; the provider
 * body it used to print is kept on the profile for the staff debug views.
 */
export const EmailSyncFailureBanner: React.FC = () => {
	const { currentUser } = useGlobalState();
	const profile = currentUser?.userprofile;

	const snoozed = useBannerSnoozed("email-sync");

	if (!profile?.email_sync_error_is_persistent || snoozed) return null;

	return (
		<EmailSyncFailureAlert
			emailMethod={profile.email_method}
			snooze={<SnoozeButton bannerKey="email-sync" />}
		/>
	);
};

/** The banner itself — also shown to staff in the navbar's active-users list,
 * exactly as its user sees it, without the snooze that only its user has. */
export const EmailSyncFailureAlert: React.FC<{
	emailMethod: EmailMethod | undefined;
	snooze?: ReactNode;
}> = ({ emailMethod, snooze }) => {
	const slug = providerSlug(emailMethod);
	const settingsHref =
		slug === "outlook" ? "/config/outlook" : `/config/email/${slug}`;

	return (
		<Alert
			severity="error"
			icon={<ErrorOutlineIcon />}
			data-component="email-sync-failure-banner"
			action={
				<Stack direction="row" spacing={1} data-component="email-sync-actions">
					<Button
						color="inherit"
						size="small"
						href={settingsHref}
						data-component="email-sync-reconnect-button"
					>
						Reconnect mailbox
					</Button>
					{snooze}
				</Stack>
			}
		>
			We haven&apos;t been able to sync your mailbox for a little while.
			Reconnecting your account should fix it.
		</Alert>
	);
};
